import { type UseQueryResult } from '@tanstack/react-query';
import { type PaginatedResponse, type WebsiteBlogPostListItem } from '@utazzo/shared';
import { ExternalLink, Eye, EyeOff } from 'lucide-react';
import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useCan } from '../lib';

import { blogPublicUrl } from './blog-url';
import { type BlogListQuery } from './blog.api';
import {
  useBlogPostsList,
  useDeleteBlogPost,
  usePublishBlogPost,
  useUnpublishBlogPost,
} from './use-blog';

import { hiddenFilterId, type DataGridColumn } from '@/components/data-grid';
import { ListScreen } from '@/components/list-screen';
import { SoftPill } from '@/components/soft-pill';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

/** Soft pill tone + dot per post status, mirroring the Pages table style. */
const STATUS_PILL: Record<string, { tone: string; dot: string; label: string }> = {
  PUBLISHED: {
    tone: 'bg-(--ds-success-bg) text-(--ds-success)',
    dot: 'bg-(--ds-success)',
    label: 'Published',
  },
  DRAFT: { tone: 'bg-(--ds-warn-bg) text-(--ds-warn)', dot: 'bg-(--ds-warn)', label: 'Draft' },
  ARCHIVED: { tone: 'bg-(--z-100) text-(--fg-muted)', dot: 'bg-(--fg-faint)', label: 'Archived' },
};

const COLUMNS: ReadonlyArray<DataGridColumn<WebsiteBlogPostListItem>> = [
  {
    id: 'title',
    header: 'Title',
    sortable: true,
    width: 420,
    truncate: false,
    cell: (row) => (
      <div className="flex flex-col gap-0.5 py-1 pr-4">
        <span className="leading-snug font-medium break-words text-(--fg)">{row.title}</span>
        <span className="text-xs text-(--fg-faint)">/blog/{row.slug}</span>
      </div>
    ),
  },
  {
    id: 'destination',
    header: 'Destination',
    cell: (row) => (
      <Link
        to="/destinations-master"
        onClick={(e) => e.stopPropagation()}
        className="text-(--fg-muted) underline-offset-2 hover:text-(--fg) hover:underline"
      >
        {row.destinationName ?? 'No destination'}
      </Link>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    cell: (row) => {
      const b = STATUS_PILL[row.status] ?? {
        tone: 'bg-(--z-100) text-(--fg-muted)',
        dot: 'bg-(--fg-faint)',
        label: row.status,
      };
      return (
        <SoftPill className={b.tone} dot dotClassName={b.dot}>
          {b.label}
        </SoftPill>
      );
    },
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    sortable: true,
    cell: (row) => (
      <span className="text-(--fg-muted) tabular-nums">
        {new Date(row.updatedAt).toLocaleString()}
      </span>
    ),
  },
];

/**
 * Adapts useBlogPostsList to the PaginatedResponse shape ListScreen expects,
 * deriving totalPages/hasNext/hasPrev from the raw pagination fields.
 */
function useAdaptedBlogPostsList(
  query: BlogListQuery,
): UseQueryResult<PaginatedResponse<WebsiteBlogPostListItem>> {
  const result = useBlogPostsList(query);
  return useMemo(() => {
    if (!result.data) {
      return result as unknown as UseQueryResult<PaginatedResponse<WebsiteBlogPostListItem>>;
    }
    const { page, pageSize, totalItems } = result.data.pagination;
    const totalPages = Math.ceil(totalItems / pageSize);
    const adapted: PaginatedResponse<WebsiteBlogPostListItem> = {
      data: result.data.data,
      pagination: {
        page,
        pageSize,
        totalItems,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
    };
    return {
      ...result,
      data: adapted,
    } as UseQueryResult<PaginatedResponse<WebsiteBlogPostListItem>>;
  }, [result]);
}

export const BlogsListPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const canWrite = useCan('blogs.write');
  const canPublish = useCan('blogs.publish');
  const canDelete = useCan('blogs.delete');

  const publish = usePublishBlogPost();
  const unpublish = useUnpublishBlogPost();
  const del = useDeleteBlogPost();

  return (
    <ListScreen<WebsiteBlogPostListItem, BlogListQuery>
      title="Blogs"
      description="Articles published on the public website. Edit content, preview, and publish."
      useList={useAdaptedBlogPostsList}
      defaultSort={{ by: 'updatedAt', dir: 'desc' }}
      defaultPageSize={20}
      toQuery={(state, base) => ({
        ...base,
        ...(state.filters.status ? { status: state.filters.status } : {}),
        ...(state.filters.completeness === 'INCOMPLETE' ? { incomplete: true } : {}),
        ...(state.filters.destination
          ? { destinationId: hiddenFilterId(state.filters.destination) }
          : {}),
        ...(state.filters.category ? { categoryId: hiddenFilterId(state.filters.category) } : {}),
      })}
      columns={COLUMNS}
      filters={[
        {
          id: 'status',
          label: 'Status',
          options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
          widthClass: 'w-44',
        },
        {
          // Deep-link target for the dashboard's Needs-attention chips - same
          // predicate the dashboard counted with, so the numbers agree.
          id: 'completeness',
          label: 'Completeness',
          options: [
            { value: '', label: 'All' },
            { value: 'INCOMPLETE', label: 'Missing info' },
          ],
          widthClass: 'w-44',
        },
        // Deep-link targets for the website dashboard's breakdown tables. Hidden: no
        // chip, just a removable pill while one is active.
        { id: 'destination', label: 'Destination', options: [], hidden: true },
        { id: 'category', label: 'Category', options: [], hidden: true },
      ]}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`/blogs/${row.id}`)}
      createButton={
        canWrite ? { label: 'Add blog', onClick: () => navigate('/blogs/new') } : undefined
      }
      rowActions={{
        extra: (row) => [
          {
            type: 'item',
            label: 'Edit',
            onSelect: () => navigate(`/blogs/${row.id}`),
          },
          {
            type: 'item',
            label: 'Preview',
            icon: <ExternalLink className="h-4 w-4" />,
            onSelect: () => window.open(blogPublicUrl(row.slug), '_blank', 'noopener,noreferrer'),
          },
          ...(canPublish
            ? [
                row.status === 'PUBLISHED'
                  ? {
                      type: 'item' as const,
                      label: 'Unpublish',
                      icon: <EyeOff className="h-4 w-4" />,
                      onSelect: () => unpublish.mutate(row.id),
                    }
                  : {
                      type: 'item' as const,
                      label: 'Publish',
                      icon: <Eye className="h-4 w-4" />,
                      onSelect: () => publish.mutate(row.id),
                    },
              ]
            : []),
        ],
        delete: {
          onDelete: (row) => del.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this blog post?',
            description: `"${row.title}" (/blog/${row.slug}) will be soft-deleted and removed from the public site.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by title or slug',
        emptyTitle: 'No blog posts yet',
        emptyDescription: 'Publish your first article to start building the blog.',
        errorTitle: 'Failed to load blog posts',
        errorDescription: 'Try refreshing or check your connection.',
      }}
    />
  );
};
